/* =============================================================================
   [KI] FAQ Editorial — page interactivity (vanilla, no framework)
   -----------------------------------------------------------------------------
   Single-section script. Idempotent — safe to run multiple times (handles
   theme-editor section reloads). All state lives on the section root, not
   on window — multiple FAQ sections on a page would each get their own
   instance (though typically there's only one).

   Behaviors:
     1. Search — filter questions by case-insensitive substring on
        question + answer text. Matches highlighted with <mark>.
     2. Empty state — when search returns nothing, show curated suggestions.
     3. Sticky scroll-spy — sidebar item highlights for the category
        nearest the top of the viewport.
     4. Open/Close all — single toggle below the sidebar.
     5. Cross-references ("Related →") — fuzzy-match question titles in
        each question's `data-ki-fp-related` attribute against actual
        question titles on the page; build clickable jump links.
     6. Jump-to-question — clicking a Start-here link or a Related link
        opens the matching <details> and scrolls it into view (accounting
        for the sticky search bar offset).

   Reduced motion: all animations are CSS-driven and respect
   prefers-reduced-motion already (in the CSS).
   ============================================================================= */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.kiFpInit === '1') return;
    root.dataset.kiFpInit = '1';

    const searchInput = root.querySelector('[data-ki-fp-search]');
    const clearBtn    = root.querySelector('[data-ki-fp-clear]');
    const content     = root.querySelector('[data-ki-fp-content]');
    const resultCount = root.querySelector('[data-ki-fp-result-count]');
    const emptyHead   = root.querySelector('[data-ki-fp-empty-headline]');
    const toggleAllBtn = root.querySelector('[data-ki-fp-toggle-all]');
    const toggleAllLbl = root.querySelector('[data-ki-fp-toggle-all-label]');
    const suggestBtns  = root.querySelectorAll('[data-ki-fp-suggest]');

    if (!content) return; // Section rendered with no questions — nothing to do.

    // ── Build a lookup of all question elements + their normalized text ──
    // Used by search, scroll-spy, and the related-link fuzzy matcher.
    const items = Array.from(content.querySelectorAll('.ki-fp-item'));
    const cats  = Array.from(content.querySelectorAll('.ki-fp-cat'));

    const itemIndex = items.map(el => {
      const qEl = el.querySelector('.ki-fp-question-text');
      const aEl = el.querySelector('.ki-fp-answer');
      return {
        el,
        id: el.id,
        question: el.dataset.kiFpQuestion || (qEl ? qEl.textContent : ''),
        questionLower: (el.dataset.kiFpQuestion || (qEl ? qEl.textContent : '')).toLowerCase(),
        answerLower: aEl ? aEl.textContent.toLowerCase() : '',
        related: (el.dataset.kiFpRelated || '').split(',').map(s => s.trim()).filter(Boolean),
      };
    });

    // ── (5) Resolve related cross-references ──
    // For each question's `related` list, find the matching question element
    // on the page and build clickable jump links inside the see-also block.
    // Fuzzy match: case-insensitive substring (in either direction).
    itemIndex.forEach(entry => {
      if (!entry.related.length) return;
      const seealso = entry.el.querySelector('[data-ki-fp-seealso]');
      const list    = entry.el.querySelector('[data-ki-fp-seealso-list]');
      if (!seealso || !list) return;

      const matches = [];
      entry.related.forEach(needle => {
        const n = needle.toLowerCase();
        const hit = itemIndex.find(other =>
          other.id !== entry.id &&
          (other.questionLower.includes(n) || n.includes(other.questionLower))
        );
        if (hit) matches.push(hit);
      });

      if (!matches.length) return; // Drop placeholder silently.

      const frag = document.createDocumentFragment();
      matches.forEach((m, i) => {
        if (i > 0) {
          const sep = document.createElement('span');
          sep.className = 'ki-fp-answer__seealso-sep';
          sep.textContent = ' · ';
          frag.appendChild(sep);
        }
        const a = document.createElement('a');
        a.className = 'ki-fp-answer__seealso-link';
        a.href = '#' + m.id;
        a.textContent = m.question;
        a.addEventListener('click', e => jumpTo(e, m.id));
        frag.appendChild(a);
      });
      list.appendChild(frag);
      seealso.removeAttribute('hidden');
    });

    // ── (6) Jump-to-question ──
    // Used by Start-here links + Related links. Opens the target <details>
    // and scrolls accounting for sticky search bar offset.
    function jumpTo(e, targetId) {
      const target = document.getElementById(targetId);
      if (!target) return;
      e.preventDefault();
      target.setAttribute('open', '');
      requestAnimationFrame(() => {
        const offset = computeStickyOffset() + 24;
        const y = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      });
    }

    function computeStickyOffset() {
      // Sticky search bar height + any global sticky-offset the theme set.
      const searchWrap = root.querySelector('.ki-fp-search-wrap');
      const sw = searchWrap ? searchWrap.offsetHeight : 0;
      const themeOffset = parseInt(
        getComputedStyle(root).getPropertyValue('--ki-fp-stick-offset') || '0', 10
      ) || 0;
      return sw + themeOffset;
    }

    // Wire Start-here links
    root.querySelectorAll('[data-ki-fp-jump]').forEach(a => {
      a.addEventListener('click', e => jumpTo(e, a.dataset.kiFpJump));
    });

    // ── (1)+(2) Search filter ──
    let searchTimer = null;

    // Build a slug → sidebar-link lookup once. The CSS uses [data-empty] on
    // these links during search to dim categories with zero hits — gives the
    // user at-a-glance feedback about where the matching results live.
    const sideLinkBySlug = {};
    root.querySelectorAll('[data-ki-fp-cat-link]').forEach(a => {
      sideLinkBySlug[a.dataset.kiFpCatLink] = a;
    });

    function applySearch(query) {
      const q = (query || '').trim().toLowerCase();
      const isSearching = q.length > 0;

      root.classList.toggle('is-searching', isSearching);
      if (clearBtn) clearBtn.hidden = !isSearching;
      if (searchInput && searchInput.value !== query) searchInput.value = query;
      // Disable toggle-all while any search query is active — it has no
      // meaningful effect when categories are filtered or hidden.
      if (toggleAllBtn) toggleAllBtn.disabled = isSearching;

      if (!isSearching) {
        // Reset all filters + highlights.
        items.forEach(el => {
          el.classList.remove('is-hidden');
          unhighlight(el);
        });
        cats.forEach(el => el.classList.remove('is-hidden'));
        // Clear the sidebar empty-marker so dimming releases.
        Object.values(sideLinkBySlug).forEach(a => a.removeAttribute('data-empty'));
        root.classList.remove('is-empty');
        if (resultCount) resultCount.textContent = '';
        // Re-enable the toggle-all button when search is cleared.
        if (toggleAllBtn) toggleAllBtn.disabled = false;
        return;
      }

      let hits = 0;
      itemIndex.forEach(entry => {
        const match = entry.questionLower.includes(q) || entry.answerLower.includes(q);
        entry.el.classList.toggle('is-hidden', !match);
        if (match) {
          hits++;
          highlight(entry.el, q);
        } else {
          unhighlight(entry.el);
        }
      });

      // Hide categories with zero visible questions, AND mirror that state
      // onto the corresponding sidebar link so the navigation reflects what's
      // actually in the result set.
      cats.forEach(cat => {
        const visible = cat.querySelectorAll('.ki-fp-item:not(.is-hidden)').length;
        const isEmpty = visible === 0;
        cat.classList.toggle('is-hidden', isEmpty);
        const slug = cat.dataset.catHead;
        const link = slug && sideLinkBySlug[slug];
        if (link) {
          if (isEmpty) link.setAttribute('data-empty', '');
          else link.removeAttribute('data-empty');
        }
      });

      // Result count + empty state.
      const isEmpty = hits === 0;
      root.classList.toggle('is-empty', isEmpty);
      if (resultCount) {
        // Templates come from schema settings via data attributes — gives
        // merchants control over the wording (and translation) without
        // editing JS. Each template can use %count% for the hit count and
        // %query% for the curly-quoted query. (We deliberately avoid {x}
        // syntax because the curly braces collide with Liquid's tag
        // delimiters when used as schema default values.) Falls back to
        // hardcoded English defaults if a template attribute is missing.
        const tplOne   = resultCount.dataset.templateOne  || '1 answer matching %query%';
        const tplMany  = resultCount.dataset.templateMany || '%count% answers matching %query%';
        const tplNone  = resultCount.dataset.templateNone || 'No matches for %query%';
        const quoted   = '“' + query + '”';
        let template;
        if (isEmpty)        template = tplNone;
        else if (hits === 1) template = tplOne;
        else                 template = tplMany;
        resultCount.textContent = template
          .replace(/%count%/g, String(hits))
          .replace(/%query%/g, quoted);
      }
      if (emptyHead && isEmpty) {
        // Append the query in quotes to the merchant-set headline.
        const base = emptyHead.dataset.kiFpEmptyBase ||
                     (emptyHead.dataset.kiFpEmptyBase = emptyHead.textContent.trim().replace(/\s*[—–-]\s*$/, ''));
        emptyHead.textContent = base.replace(/[.!?]\s*$/, '') + ' “' + query + '”.';
      }
    }

    function highlight(itemEl, q) {
      // Highlight only the question text + first answer paragraph.
      // Strip any prior marks first.
      unhighlight(itemEl);
      const targets = [
        itemEl.querySelector('.ki-fp-question-text'),
        ...itemEl.querySelectorAll('.ki-fp-answer p'),
      ].filter(Boolean);
      const re = new RegExp('(' + escapeRe(q) + ')', 'gi');
      targets.forEach(node => {
        wrapMatches(node, re);
      });
    }

    function unhighlight(itemEl) {
      itemEl.querySelectorAll('mark.ki-fp-hit').forEach(m => {
        const parent = m.parentNode;
        while (m.firstChild) parent.insertBefore(m.firstChild, m);
        parent.removeChild(m);
        parent.normalize();
      });
    }

    function wrapMatches(node, re) {
      // Walk text nodes and replace matches with <mark> wrappers.
      // Skip nodes that already contain <mark>, <a>, or are inside one.
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
      const textNodes = [];
      let n;
      while ((n = walker.nextNode())) textNodes.push(n);
      textNodes.forEach(tn => {
        if (!re.test(tn.nodeValue)) { re.lastIndex = 0; return; }
        re.lastIndex = 0;
        const frag = document.createDocumentFragment();
        let last = 0;
        let m;
        while ((m = re.exec(tn.nodeValue))) {
          if (m.index > last) frag.appendChild(document.createTextNode(tn.nodeValue.slice(last, m.index)));
          const mark = document.createElement('mark');
          mark.className = 'ki-fp-hit';
          mark.textContent = m[0];
          frag.appendChild(mark);
          last = m.index + m[0].length;
        }
        if (last < tn.nodeValue.length) frag.appendChild(document.createTextNode(tn.nodeValue.slice(last)));
        tn.parentNode.replaceChild(frag, tn);
      });
    }

    function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

    if (searchInput) {
      searchInput.addEventListener('input', e => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => applySearch(e.target.value), 80);
      });

      // Mobile-aware placeholder. On narrow viewports the long desktop
      // placeholder ("Search the FAQ — try ‘elasticity’, ‘shipping’,
      // ‘pregnancy’") truncates mid-word; the merchant can set a shorter
      // mobile version that reads cleanly. Both come in via data attrs;
      // we swap the actual `placeholder` attribute on breakpoint change.
      //
      // matchMedia fires only when the boolean state changes — no
      // throttling needed, no work on incidental resize events. The 900px
      // breakpoint matches the @media query in the CSS.
      const mq = window.matchMedia('(max-width: 900px)');
      const applyPlaceholder = () => {
        const desktop = searchInput.dataset.placeholderDesktop || '';
        const mobile  = searchInput.dataset.placeholderMobile  || desktop;
        searchInput.placeholder = mq.matches ? mobile : desktop;
      };
      applyPlaceholder();
      // addEventListener('change') is the modern API; addListener is the
      // pre-Safari-14 fallback. Some bundlers/polyfills need both. The
      // try/catch absorbs the case where neither is available (very old
      // browsers) — placeholder just stays at its initial value.
      try {
        if (mq.addEventListener) mq.addEventListener('change', applyPlaceholder);
        else if (mq.addListener) mq.addListener(applyPlaceholder);
      } catch (e) { /* old browser, no live swap */ }
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        applySearch('');
        if (searchInput) { searchInput.value = ''; searchInput.focus(); }
      });
    }
    suggestBtns.forEach(btn => {
      btn.addEventListener('click', () => applySearch(btn.dataset.kiFpSuggest));
    });

    // ── (3) Sidebar active state ──
    // Two behaviors layered:
    //
    //   A. Scroll-spy: as the user scrolls, the sidebar highlights whichever
    //      category is currently parked at the top of the read area. Built
    //      with IntersectionObserver against a thin "read zone" strip just
    //      below the sticky search bar.
    //
    //   B. Click suppression: when the user clicks a sidebar link, the
    //      browser smooth-scrolls to that anchor. During that animated
    //      scroll, the spy would see the scroll passing through 2, 3, 4
    //      categories and update the highlight at each — visible cascade
    //      of changes from one click. Bad UX. So we suppress spy from the
    //      moment of click until the scroll has settled at rest (no scroll
    //      events for 150ms — empirically reliable across smooth-scroll
    //      durations and browser implementations).
    //
    // The "settled at rest" detection beats fixed-duration locks (700ms,
    // etc.) because it adapts to actual scroll length: short scrolls
    // release fast, long scrolls hold the lock until they actually finish.
    const sideLinks = Array.from(root.querySelectorAll('[data-ki-fp-cat-link]'));

    function setActive(slug) {
      sideLinks.forEach(a => {
        a.classList.toggle('is-active', a.dataset.kiFpCatLink === slug);
      });
    }

    // Suppression flag and settle-detection. When the click handler sets
    // suppressed=true, the spy callback is a no-op. The settle detector
    // (a debounced scroll listener) flips it back to false once the scroll
    // has been idle for 150ms.
    let suppressed = false;
    let settleTimer = null;
    function armSettleDetector() {
      // Each scroll event during the suppression window resets the timer.
      // When 150ms passes with no scroll events, we declare "settled" and
      // release the lock. Spy can now respond to genuine user scrolling.
      const onScroll = () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          suppressed = false;
          window.removeEventListener('scroll', onScroll);
        }, 150);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      // Kick the timer in case no scroll events fire (click on a link
      // that targets the already-visible position, for example).
      onScroll();
    }

    if (sideLinks.length && cats.length && 'IntersectionObserver' in window) {
      const inZone = new Set();

      const recompute = () => {
        if (suppressed) return;
        if (root.classList.contains('is-searching')) return;
        if (!inZone.size) return;
        // Pick the in-zone category whose top is largest (i.e., closest
        // to / just below the strip's top edge). That's the category
        // currently parked at the read position, not the one already
        // scrolled past.
        let current = null;
        let bestTop = -Infinity;
        inZone.forEach(cat => {
          const t = cat.getBoundingClientRect().top;
          if (t > bestTop) { bestTop = t; current = cat; }
        });
        if (current) setActive(current.dataset.catHead);
      };

      const buildObserver = () => {
        const stickyOffset = computeStickyOffset();
        // Read zone: thin horizontal strip just below the sticky search bar.
        // - Top: shave off the search bar height
        // - Bottom: shave off everything more than ~200px below the search bar
        // A category is "in zone" when any part of it overlaps this strip.
        const rootMarginTop    = -stickyOffset;
        const rootMarginBottom = -Math.max(100, window.innerHeight - stickyOffset - 200);
        return new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) inZone.add(entry.target);
            else inZone.delete(entry.target);
          });
          recompute();
        }, {
          rootMargin: `${rootMarginTop}px 0px ${rootMarginBottom}px 0px`,
          threshold: 0,
        });
      };

      let observer = buildObserver();
      cats.forEach(c => observer.observe(c));

      // Click handler: set active immediately, then suppress spy until
      // the scroll settles at rest.
      sideLinks.forEach(a => {
        a.addEventListener('click', () => {
          const slug = a.dataset.kiFpCatLink;
          if (!slug) return;
          setActive(slug);
          suppressed = true;
          armSettleDetector();
        });
      });

      // Resize: rebuild observer because the sticky offset and viewport
      // height both change, so the rootMargin needs recomputing.
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          observer.disconnect();
          inZone.clear();
          observer = buildObserver();
          cats.forEach(c => observer.observe(c));
        }, 150);
      }, { passive: true });

      // Initial state.
      setActive(sideLinks[0]?.dataset.kiFpCatLink);
    } else if (sideLinks.length) {
      // Fallback for browsers without IntersectionObserver. Same scroll
      // event approach with the same suppression semantics.
      function spy() {
        if (suppressed) return;
        if (root.classList.contains('is-searching')) return;
        const offset = computeStickyOffset() + 160;
        let current = sideLinks[0]?.dataset.kiFpCatLink;
        cats.forEach(c => {
          const top = c.getBoundingClientRect().top;
          if (top - offset < 0) current = c.dataset.catHead;
        });
        setActive(current);
      }
      sideLinks.forEach(a => {
        a.addEventListener('click', () => {
          const slug = a.dataset.kiFpCatLink;
          if (!slug) return;
          setActive(slug);
          suppressed = true;
          armSettleDetector();
        });
      });
      window.addEventListener('scroll', spy, { passive: true });
      spy();
    }

    // ── (4) Open/Close all toggle ──
    if (toggleAllBtn) {
      let allOpen = false;
      toggleAllBtn.addEventListener('click', () => {
        allOpen = !allOpen;
        items.forEach(d => {
          if (allOpen) d.setAttribute('open', '');
          else d.removeAttribute('open');
        });
        if (toggleAllLbl) {
          // Read both possible labels from data attrs if the merchant set them;
          // fall back to the current text + a sensible flip.
          toggleAllLbl.textContent = allOpen
            ? (toggleAllBtn.dataset.kiFpCloseAll || 'Collapse all')
            : (toggleAllBtn.dataset.kiFpOpenAll || 'Open all');
        }
      });
    }

    // ── Theme-editor compat ──
    // When the merchant edits a block in the editor, Shopify reloads the
    // section. Listen for the editor's reorder/load events so we re-init.
    document.addEventListener('shopify:section:load', e => {
      if (e.target && e.target.contains(root)) init(root);
    });
  }

  // Initialize all instances on the page.
  function initAll() {
    document.querySelectorAll('[data-section-type="ki-faq-editorial"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor — re-init when the section is added/reloaded.
  document.addEventListener('shopify:section:load', initAll);
})();